// =============================================================================
// WellFinder Report model.
//
// Produces the WellFinder deliverable — the .docx report and a companion File
// Geodatabase in one .zip — via POST /api/v1/wellfinder-report/bundle.
//
// Both artifacts come from ONE request against one manifest and one as_of. The
// geodatabase is not a reproduction of the pipeline's .gdb: it is a platform
// export of the same detections, and it carries records the pipeline artifact
// never had. It says so in its own embedded provenance table.
//
// This path is ~1.2 s rather than the document's ~110 ms, most of it the
// geodatabase write. ActionPreview's existing syncStatus spinner covers it.
//
// SCOPE IS A SAVED AOI, NOT THE VIEWPORT. The document's stated scope — area,
// bounding box, county, state, county code — and the filter that selects its
// detections are derived from ONE stored geometry server-side, so what the
// report says it covers and what it counted are the same object rather than two
// things kept in agreement by hand. A viewport has no stored geometry to derive
// any of that from, and it changes with window size and zoom, so the same
// request would not reproduce later. The route rejects bbox with 400; the
// registry declares requiresAoi so the run is blocked in the UI first, with an
// explanation, rather than surfacing a server error.
//
// The PDF/CSV route (/api/v1/wellfinder-report) is UNCHANGED and still serves
// the WellFinder chat in HeliosDock, including its async job path. This action
// no longer calls it.
// =============================================================================

// NO DRAFT REQUEST (DB 2026-10-25). This asked for the prototype marking on
// every report while review state went unrecorded. The platform records it now
// (wellfinder_well_record.status, the view's qc_status since migration 112), and
// the marking that depends on the data stays where it is enforced: the bundle
// route forces it when any record carries no review state, and the generator
// when review state is synthetic. No report_version either: it is the
// generator's REPORT_VERSION.

export async function runWellfinderReport({ scope, fetchWithAuth, options = {} }) {
  const aoiId = scope?.kind === 'aoi' ? scope.aoi?.id : null;
  if (!aoiId) {
    throw new Error(
      'This report needs a saved AOI. Pick one under Scope — a viewport changes with ' +
      'window size and zoom, so the report could not be reproduced.',
    );
  }

  // Self-serve: this org is the subject, not a customer a vendor delivered to.
  // The cover reads "AREA REPORT / <org>", which is what the data supports — an
  // engagement cover would name DreamHammer as both preparer and recipient.
  // Switch to 'engagement' only alongside a real client_name for the customer.
  //
  // No client_name is sent, deliberately: it overrides the org-derived cover
  // name (from-platform.js), and the value on hand here is the active PROJECT
  // name, which is not who generated the report.
  const body = {
    aoi_id: aoiId,
    profile: 'self-serve',
    // Always sent, both keys, as booleans: the route rejects anything else, and
    // an explicit value means the report states what was asked for rather
    // than what the server defaults to.
    filters: reportFilters(options),
  };

  const response = await fetchWithAuth('/api/v1/wellfinder-report/bundle', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    throw new Error(await readError(response));
  }

  const disposition = response.headers.get('Content-Disposition') ?? '';
  const filename = disposition.match(/filename="?([^";\n]+)"?/)?.[1]
    ?? 'wellfinder-report.zip';

  triggerDownload(await response.blob(), filename);
  return { filename };
}

/**
 * The switches, as the bundle route's `filters`. The default matches the
 * server's: unfiltered. (include_no_well went with the no-well layer when the
 * geodatabase moved to the customer's exemplar schema, 2026-10-01.)
 */
export function reportFilters(options = {}) {
  return {
    rrc_producing_match: options.rrc_producing_match === true,
  };
}

/**
 * Turn an error response into something a person can act on.
 *
 * 422 matters most here. It is not a fault — the request is well formed and the
 * platform is healthy — it means the data cannot support a claim the document
 * would make, and the generator's `detail` names exactly which figure and why.
 * That text is the useful part, so it is surfaced verbatim rather than replaced
 * with a status code. It is multi-paragraph by design; the panel renders it as
 * written.
 */
async function readError(response) {
  let payload = null;
  try {
    payload = await response.json();
  } catch {
    return `Report generation failed (${response.status}).`;
  }

  const { error, detail } = payload ?? {};

  if (response.status === 422) {
    return [detail || error || 'The data in scope cannot support this report.']
      .join('\n')
      .trim();
  }

  if (response.status === 501) {
    return detail
      ? `${error ?? 'Report generator unavailable'}\n\n${detail}`
      : (error ?? 'The report generator is not available in this deployment.');
  }

  if (response.status === 400) return error ?? 'Invalid report request.';
  if (response.status === 404) return error ?? 'AOI not found.';

  return error ? `${error}${detail ? `\n\n${detail}` : ''}` : `Server error ${response.status}`;
}

/**
 * Lightweight count of wells in scope.
 * Used by the Action Preview to show scope awareness before running.
 *
 * Still answers for a viewport even though the report cannot run on one — the
 * count is what tells a user whether the area they are looking at is worth
 * pinning as an AOI in the first place.
 */
export async function getWellfinderReportScopeCount({ scope, fetchWithAuth }) {
  const response = await fetchWithAuth('/api/v1/wellfinder-scope-count', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(scopeToBody(scope)),
  });

  if (!response.ok) {
    throw new Error(`Count failed (${response.status})`);
  }
  const data = await response.json();
  return data.count ?? 0;
}

function scopeToBody(scope) {
  if (scope.kind === 'aoi') return { aoi_geom: scope.aoi.geom };
  if (scope.kind === 'viewport') return { bbox: scope.bbox };
  throw new Error(`Unknown scope kind: ${scope.kind}`);
}

function triggerDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
